/* eslint-disable react-hooks/exhaustive-deps */
import Axios from 'axios';
import React, {useEffect, useReducer} from 'react';
import {generatePath, useHistory, useParams} from 'react-router';
import {generateCoords, generateRectCoords} from '../../utilities';
import ImageMapper from '../ImageDrawer';
import Info from '../Info';
import Modal from '../Modal';
import HallSelection from '../HallSelection';
import {useTranslation, withTranslation} from 'react-i18next';
import i18n from '../../i18n';
import MapNameNavigator from "../MapNavigator";

let url = window.location.origin;

const HallSelectionTrans = withTranslation()(HallSelection);

function App(props) {
    let host = '';
    if (process.env.NODE_ENV === "development") {
        // host = 'http://localhost:8080';
        host = 'https://maps.eyeled-services.de';
        url = host;
    }

    let {lang, projectId, standId, mapId, areaId, locationX, locationY} = useParams();
    let history = useHistory();
    document.documentElement.lang = lang;

    setupPrimaryColor(projectId);

    if (mapId) {
        mapId = decodeURIComponent(mapId);
    }
    if (areaId) {
        areaId = decodeURIComponent(areaId);
    }
    if (standId) {
        standId = decodeURIComponent(standId);
    }
    if (locationX) {
        locationX = decodeURIComponent(locationX);
    }
    if (locationY) {
        locationY = decodeURIComponent(locationY);
    }

    useEffect(() => {
        i18n.changeLanguage(lang);
    }, [lang]);

    const [store, setStore] = useReducer(
        (oldStore, newStore) => ({...oldStore, ...newStore}),
        {
            show: 'none',
            MAP: {
                name: 'All-Element-Map',
                areas: [],
            },
            modalTitle: null,
            modalContent: null,
            map: null,
            mapElements: null,
            width: 900,
            imgWidth: 3072,
            companies: null,
            events: null,
            urls: null,
            maps: null,
            src: '',
            loading: false,
            selectedTop: 0,
            selectedLeft: 0,
            selectedMaps: null,
        }
    );

    function setupPrimaryColor(projectId) {
        let primaryColor = undefined;
        let buttonColor = undefined;
        if (projectId.startsWith("kwf")) {
            primaryColor = '#389e37';
            buttonColor = '#389e37';
        } else if(projectId.startsWith("spiel")) {
            primaryColor = '#007ec1';
            buttonColor = '#007ec1';
        } else if(projectId.startsWith("rkt")) {
            primaryColor = '#00b48d';
            buttonColor = '#00b48d';
        }
        if (primaryColor !== undefined) {
            document.documentElement.style.setProperty('--primary-color',primaryColor);
        }
        if (buttonColor !== undefined) {
            document.documentElement.style.setProperty('--button-color',buttonColor);
        }
    }

    function mapCompare(left, right, prioritizeFloor, floorDescending) {
        let result;

        if (left['ID'] === '0') {
            return -1;
        } else if (right['ID'] === '0') {
            return 1;
        }

        if (prioritizeFloor || left['PARENT_ELEMENT_ID'] === right['PARENT_ELEMENT_ID']) {
            result = compareBasedOnFloor(left, right, floorDescending);
        } else {
            result = compareBasedOnNameDotNotation(left, right, floorDescending);
        }

        if (result === 0) {
            return left['NAME'].localeCompare(right['NAME']);
        } else {
            return result;
        }
    }

    function compareBasedOnFloor(left, right, floorDescending) {
        if (floorDescending) {
            return right['FLOOR'] - left['FLOOR'];
        } else {
            return left['FLOOR'] - right['FLOOR'];
        }
    }

    // weird sort function to properly sort map names like '3.0', '3.Via', '3.1', '4.0', '4.1', ... (example of fbm22)
    // at first, we compare the numeric values before the dot and if they are identical we will execute a map.floor comparison
    // in the example from above '3.Via' is the floor between '3.0' and '3.1'
    function compareBasedOnNameDotNotation(left, right, floorDescending) {
        if (left['NAME'].includes('.') || right['NAME'].includes('.')) {
            const leftPreDotDigits = left['NAME'].split('.')[0].match(/\d+/);
            const rightPreDotDigits = right['NAME'].split('.')[0].match(/\d+/);

            if (leftPreDotDigits && rightPreDotDigits) {
                const leftPreDotInt = parseInt(leftPreDotDigits);
                const rightPreDotInt = parseInt(rightPreDotDigits);

                if (!isNaN(leftPreDotInt) && !isNaN(rightPreDotInt)) {
                    let result = leftPreDotInt - rightPreDotInt;

                    if (result === 0) {
                        result = compareBasedOnFloor(left, right, floorDescending);
                    }

                    return result;
                }
            } else {
                return leftPreDotDigits === rightPreDotDigits ? 0 : leftPreDotDigits ? -1 : 1;
            }
        }
        return 0;
    }

// fetch data when url changed
    useEffect(() => {
        // console.log('Effect Variables :', lang, projectId, standId, mapId, areaId);
        const getData = () => {
            let axiosUrl;
            axiosUrl = `${url}/${lang}/${projectId}/mapData`;
            if (mapId) {
                let encodedMapId = encodeURIComponent(mapId);
                axiosUrl += `/map/${encodedMapId}`;
            } else if (standId) {
                let encodedStandId = encodeURIComponent(standId);
                axiosUrl += `/stand/${encodedStandId}`;
            } else if (areaId) {
                let encodedAreaId = encodeURIComponent(areaId);
                axiosUrl += `/area/${encodedAreaId}`;
            }

            Axios(axiosUrl)
                .then(async ({data: {map, maps, mapElements, companies, events, urls}}) => {
                    maps.sort((left, right) => {
                        return mapCompare(left, right, false, false);
                    });
                    if (!store.map || map.ID !== store.map.ID) {
                        // preload map image
                        let src = `${host}/${map.path}/maps/${map.ID}.png`;
                        const imgPromise = new Promise(function (resolve, reject) {
                            const img = new Image();
                            img.onload = function () {
                                resolve(img)
                            };
                            img.onerror = img.onabort = function () {
                                reject(src)
                            };
                            img.src = src;
                        });
                        await imgPromise;

                        setStore({map, mapElements});
                    } else if (store.MAP) {
                        store.MAP.areas.forEach(item => item.preFillColor = null);
                    }
                    if (map.NAME && areaId) {
                        const area = mapElements.filter((item) => item.ID === areaId);
                        if (area && area.length === 1) {
                            setStore({modalTitle: map.NAME + (area[0].NAME ? ' ' + area[0].NAME : '')});
                        } else {
                            setStore({modalTitle: null});
                        }
                    }

                    setStore({maps, companies, events, urls, loading: false});
                })
                .catch((err) => {
                        console.log('Error: ', err);
                        if (standId) {
                            setStore({error: 'standViewNotAvailable'});
                        } else {
                            setStore({error: 'viewNotAvailable'});
                        }
                    }
                )
            ;
        };

        if (lang && projectId && (standId || mapId || areaId)) {
            setStore({loading: true});
            getData();
        }

        return () => {
            // cleanup
        };
    }, [lang, projectId, standId, mapId, areaId]);

    // Set the map and the map elements
    useEffect(() => {
        if (!store.mapElements) return;
        var areas = store.mapElements.map((element) => {
            const {XCOORDS, YCOORDS, ID, X, Y, W, H, STAND_ID, NAME} = element;
            let coords;
            if (
                !XCOORDS ||
                !YCOORDS ||
                XCOORDS.split('|').map(Number).length < 3 ||
                YCOORDS.split('|').map(Number).length < 3
            ) {
                coords = generateRectCoords(X, Y, W, H);
            } else {
                coords = generateCoords(XCOORDS, YCOORDS);
            }

            const area = {
                shape: (XCOORDS && YCOORDS) ? 'poly' : 'rect',
                x: X,
                y: Y,
                width: W,
                height: H,
                coords: coords,
                id: ID,
                /*fillColor: getRandomColor(),*/
                preFillColor: (standId && STAND_ID === standId) ? '#ff0000aa' : null,
                name: NAME,
            };
            return area;
        });
        
        if (locationX && locationY) {
            const area = {
                shape: 'circle',
                x: locationX,
                y: locationY,
                width: 150,
                height: 150,
                coords: [locationX, locationY, 40],
                /*fillColor: getRandomColor(),*/
                preFillColor: '#ff0000ff',
                name: 'location',
            };
            areas.push(area);
        }

        const MAP = {
            name: `${store.map.ID}`,
            displayName: `${store.map['NAME']}`,
            areas: areas,
        };

        const {W, H, ID, path} = store.map;
        var width = W;
        const src = `${host}/${path}/maps/${ID}.png`;
        if (width > window.innerWidth) {
            width = window.innerWidth;
        }
        var scale = width / W;
        if (scale * H > window.innerHeight) {
            scale = window.innerHeight / H;
            width = scale * W;
        }
        setStore({MAP, width, imgWidth: W, src});

        return () => {
            // cleanup
        };
    }, [store.map]);

    // Display companies, events & urls
    useEffect(() => {
        if ((!store.companies && !store.events && !store.urls) || standId) return;
        if (!standId && !areaId) {
            setStore({show: 'none'});
            return;
        }
        const info = (
            <Info
                companies={store.companies}
                events={store.events}
                urls={store.urls}
                loading={store.loading}
                map={store.map}
                host={host}
            />
        );
        setStore({modalContent: info, show: 'block'});

        return () => {
            // cleanup
        };
    }, [store.companies, store.events, store.urls]);

    const handleClick = (x, y, area) => {
        const {id} = area;
        //Make sure not to display modal on POI
        if (id.indexOf('POI') !== -1) return setStore({show: 'none'});

        // Change stand
        if (areaId !== id) {
            setStore({selectedMaps: null});
            const candidates = store.maps.filter((item) =>
                item.PARENT_ELEMENT_ID.split("|").includes(id));
            if (candidates.length === 1) {
                gotoMap(candidates[0]);
            } else if (candidates.length > 0) {
                candidates.sort((left, right) => {
                    return mapCompare(left, right, true, true);
                })
                var child = document.getElementById('imageMapper');
                var offsetLeft = x;
                var offsetTop = y;
                if (window.innerWidth < 600) {
                    offsetLeft = 10;
                    offsetTop = 10;
                } else if (child) {
                    offsetLeft += child.offsetLeft - child.scrollLeft;
                    offsetTop += child.offsetTop - child.scrollTop;
                    if (offsetTop < 80) {
                        offsetTop = 80;
                    }
                }
                setStore({selectedMaps: candidates, selectedTop: offsetTop, selectedLeft: offsetLeft});
            } else {
                history.replace({
                    pathname: generatePath('/:lang/:projectId/area/:areaId', {
                        projectId: projectId,
                        lang: lang,
                        areaId: id,
                    }),
                });
            }
        } else {
            const info = (
                <Info
                    companies={store.companies}
                    events={store.events}
                    urls={store.urls}
                    loading={store.loading}
                    map={store.map}
                    host={host}
                />
            );
            setStore({modalContent: info, show: 'block'});
        }
    };

    const gotoMap = (map) => {
        history.push({
            pathname: generatePath('/:lang/:projectId/map/:mapId', {
                projectId: projectId,
                lang: lang,
                mapId: map.ID,
            }),
        });
    };

    // Map actions
    const handleMouseEnter = (area) => {
    };
    const handleMouseLeave = (area) => {
    };
    const handleMouseMove = (area, _, evt) => {
    };
    const handleImageClick = (evt) => {
    };
    const handleImageMouseMove = (evt) => {
    };
    const handleLoad = () => {
    };

    const handleHallSelection = (map) => {
        if (map != null) {
            gotoMap(map);
        }
        setStore({selectedMaps: null});
    };

    const {t} = useTranslation();

    return (
        <div className="grid">
            <div className="presenter">
                <div id="presenterChild" style={{position: 'relative'}}>
                    <div>
                        {(!store.map || (store.loading && mapId) || store.error) ? (
                            <div style={{
                                height: '100vh',
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}><h3>{store.error ? t(store.error) : t('loading')}</h3></div>
                        ) : (
                            <div>
                                <ImageMapper
                                    src={store.src}
                                    map={store.MAP}
                                    width={store.width}
                                    imgWidth={store.imgWidth}
                                    onLoad={() => handleLoad()}
                                    onClick={(x, y, area) => handleClick(x, y, area)}
                                    onMouseEnter={(area) => handleMouseEnter(area)}
                                    onMouseLeave={(area) => handleMouseLeave(area)}
                                    onMouseMove={(area, _, event) => handleMouseMove(area, _, event)}
                                    onImageClick={(event) => handleImageClick(event)}
                                    onImageMouseMove={(event) => handleImageMouseMove(event)}
                                    setStore={setStore}
                                />

                                <HallSelectionTrans maps={store.selectedMaps}
                                                    onSelection={(map) => handleHallSelection(map)}
                                                    top={store.selectedTop} left={store.selectedLeft}/>
                            </div>
                        )}

                        {store.map && store.maps &&
                            <MapNameNavigator maps={store.maps} selectedMap={store.map}
                                              onSelection={(map) => handleHallSelection(map)}/>}

                        {store.show === 'block' && (
                            <Modal
                                title={store.modalTitle}
                                content={store.modalContent}
                                show={store.show}
                                setStore={setStore}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default App;
